import { token } from '@/infrastructure/container';

// The digest a share key is recorded under. Only the hash ever reaches the project aggregate: that
// registry is readable by every contributor in the building, so a bearer key in there is a bearer
// key handed to the whole hall (docs/sync.md).
//
// A service and not a bare function because the real one is WebCrypto, which is async, absent under
// a plain node runner and awkward to reach from a store. The store takes this interface and stays
// agnostic about the algorithm; a test hands it a deterministic fake.

export interface IHasher {
  sha256Hex(input: string): Promise<string>;
}

export const TOKEN_IHasher = token<IHasher>('IHasher');

// `crypto.subtle`, which the browser and Node 22 both ship, so this needs no dependency. Lowercase
// hex, no salt: the admin device that mints a key and the phone that redeems it must compute the
// same digest, and a salt only one of them knows makes the two disagree. There is nothing to
// strengthen against anyway — the input is a high-entropy bearer key, not a password.
//
// WebCrypto is exposed only in a secure context, so a LAN IP over plain HTTP has no `crypto.subtle`
// at all. That is the same constraint `Identity` already lives under and vite.config.ts already
// serves dev over TLS for.
export class WebCryptoHasher implements IHasher {
  async sha256Hex(input: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  }
}
